import { useState, useEffect, useCallback, useContext } from "react";
import "./breakdown.scss";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";

// context
import { AuthContext } from "../../../../contexts/authContext";

// icon
import { leftArrowIcon } from "../../../../constants/icons";

// component
import Loaders from "../../../../components/UI/Loaders/Loaders";
import PieChartComponent from "../../../../components/Charts/PieChartComponent/PieChartComponent";
import RadarChartComponent from "../../../../components/Charts/RadarChartComponent/RadarChartComponent";
import HorizontalStackedBar from "../../../../components/Charts/HorizontalStackedBar/HorizontalStackedBar";

// data
import {sectionAbbreviation} from "./sectiondata";

interface SectionResponse {
  section_id: string;
  section: string;
  color: string;
  total_response: number;
  responses: {
    option: string;
    count: number;
    percent: number;
  }[];
}

interface OverviewData {
  name: string;
  value: number;
}

const API_URL = "/api/v1";

const Breakdown = () => {
  const {setUser} = useContext(AuthContext)
  const { id } = useParams();
  const navigate = useNavigate();
  const [systeminfo, setSysteminfo] = useState({
    name: "",
    evaluation_type: "",
  });
  const [sectionResponseData, setSectionResponseData] = useState<
    SectionResponse[]
  >([]);
  const [overview, setOverview] = useState<OverviewData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [radarData, setRadarData] = useState<
    { option: string; count: number; percent: number }[]
  >([]);

  const fetchEvaluationBreakdown = useCallback(async () => {
    try {
      let radData: { id: string; option: string; count: number; percent: number }[] = [];

      const res = await axios.get(
        `${API_URL}/evaluation/detailed/breakdown/${id}`
      );

      const sectionRes = await axios.get(`${API_URL}/catalog/sections/list`);
      if (sectionRes.status === 200 && sectionRes.data.success === true) {
        if (sectionRes.data.sections.length > 0) {
          sectionRes.data.sections.forEach((sec: any) => {
            radData.push({
              id: sec.value,
              option: sec.label,
              count: 0,
              percent: 0
            });
          });
        }
      }

      if (res.status === 200 && res.data.success === true) {
        setSysteminfo(res.data.system_info);
        setOverview([
          { name: "Yes", value: res.data.overview_data.total_yes_count },
          { name: "No", value: res.data.overview_data.total_no_count },
          { name: "N/A", value: res.data.overview_data.total_na_count },
          { name: "TBD", value: res.data.overview_data.total_tbd_count },
          {
            name: "Unanswered",
            value: res.data.overview_data.total_unanswered_count,
          },
        ]);

        let updatedRadDataList: { option: string; count: number; percent:number }[] = [];
        radData.forEach((sect) => {
          let sectionIndex = res.data.section_response_data.findIndex(
            (sc: any) => {
              return sc.section_id.trim() === sect.id.trim();
            }
          );
          let sectionName = sect.option;
          let sectionNameIndex = sectionAbbreviation.findIndex((sn:any) =>{
            return sn.label.trim() === sect.option.trim()
         })
         if(sectionNameIndex >= 0){
          sectionName = sectionAbbreviation[sectionNameIndex].value
         }

          let data = {
            option: sectionName,
            count: sect.count,
            percent: sect.percent
          };
          if (sectionIndex >= 0) {
            data.count = parseFloat(res.data.section_response_data[sectionIndex].responses[0].count);
            data.percent = parseFloat(res.data.section_response_data[sectionIndex].responses[0].percent);
          }
          updatedRadDataList.push(data)
        });
        res.data.section_response_data.forEach((sect: any) => {});
        setRadarData(updatedRadDataList);
        setSectionResponseData(res.data.section_response_data);
      }
    } catch (error:any) {
      console.log("Error fetching breakdown information:", error);
      if(error.response){
        const {status} = error.response;
        if(status === 401 || status === 500){
          setUser(null)
        }
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    fetchEvaluationBreakdown();
    // eslint-disable-next-line
  }, [fetchEvaluationBreakdown]);

  return (
    <div className="breakdown">
      <div className="content">
        <div className="appbar">
          <span
            onClick={() => {
              navigate(`/dashboard/evaluation/questionnaire/${id}`);
            }}
          >
            {leftArrowIcon} Back to questionnaire
          </span>
        </div>
        {loading ? (
          <Loaders />
        ) : (
          <div className="contentContainer">
            <div className="titleContainer">
              <span>Detailed breakdown for</span>
              <div className="nameHolder">
                <span>{systeminfo.name}</span>
                <div
                  className={`typeBadge ${
                    systeminfo.evaluation_type === "guided-approach" &&
                    "gradient"
                  }`}
                >
                  {systeminfo.evaluation_type === "guided-approach"
                    ? "Guided approach questionnaire"
                    : "User Driven questionnaire"}
                </div>
              </div>
            </div>
            <div className="sectionDivider">
              <span>Overview</span>
            </div>
            <div className="multiChartHolder">
              <PieChartComponent data={overview} />
              <RadarChartComponent dataset={radarData} color="#3D424A" />
            </div>

            <div className="sectionDivider" style={{ marginTop: 30 }}>
              <span>Breakdown by Section</span>
            </div>
            <HorizontalStackedBar
                dataset={sectionResponseData}
              />
          </div>
        )}
      </div>
    </div>
  );
};

export default Breakdown;
